import * as THREE from 'three';
import type { BuildPieceDef } from './catalog';
import { pieceDef } from './catalog';
import { hostAnchorsOf } from './anchors';
import { S, TRI_R, edgesFor, rotXZ, type FootprintShape } from './grid';
import type { RoomDebugCell } from './roomGraph';
import type { PlacedPiece, PlacementReason, ResolvedSlot } from './types';

// Build-mode debug gizmos: anchor dots colored by family, outward-normal arrows, occupied-vs-free
// slot markers, a tiny support bar over each placed piece, and a one-line readout of the LAST
// placement rejection reason. It is OPT-IN — nothing in the facade turns it on unless setEnabled is
// flipped (the facade wires that to the existing debug overlay toggle), so the production path pays
// nothing. rebuild() is called on each base change (place / collapse); the ghost-slot preview and the
// reason line are refreshed every update() while building (cheap: a few markers + a textContent set).

const FAMILY_COLOR: Record<string, number> = {
  ground: 0x8888ff,
  ground_edge_square: 0x44ccff,
  ground_edge_triangle: 0x44ffcc,
  wall_base: 0xffcc44,
  wall_top_square: 0xff8844,
  wall_top_triangle: 0xff4488,
  floor_edge_square: 0x88ff44,
  floor_edge_triangle: 0x44ff88,
  door_host: 0xff44ff,
  floor_top: 0xcccccc,
};

const SLOT_FREE = 0x33dd55;     // a slot the ghost would reserve that is currently clear
const SLOT_TAKEN = 0xdd3333;    // an occupied slot (placed) or a ghost slot that conflicts

const ROOM_SEALED = 0x2fd06a;   // room-view cell tint: fully sealed room (privacy view active inside)
const ROOM_LEAKY = 0xe08a2f;    // room-view cell tint: enclosure leaks (bare edge / missing lid / low wall)

export class BuildDebug {
  readonly group = new THREE.Group();
  enabled = false;
  lastReason: PlacementReason = 'OK';

  private readonly dotGeo = new THREE.SphereGeometry(0.08, 6, 6);
  private readonly slotGeo = new THREE.BoxGeometry(0.22, 0.22, 0.22);
  // The placed-piece gizmos (anchors / slots / support bars) — rebuilt only on a base change.
  private readonly placedGroup = new THREE.Group();
  // The ghost's pending-slot preview — refreshed every frame while building.
  private readonly ghostGroup = new THREE.Group();
  // The ROOM VIEW: instanced floor-height quads tinting each room's cells (sealed green, leaky
  // orange). Rebuilt on base changes only; geometry/material shared across rebuilds.
  private readonly roomsGroup = new THREE.Group();
  private readonly roomSquareGeo = new THREE.PlaneGeometry(S * 0.92, S * 0.92).rotateX(-Math.PI / 2);
  // A 3-segment circle IS an equilateral triangle; the extra rotateY points its corner at local +z
  // like the grid's TRI_CORNERS, so the tint tracks the cell's yaw.
  private readonly roomTriGeo = new THREE.CircleGeometry(TRI_R * 0.85, 3).rotateX(-Math.PI / 2).rotateY(Math.PI);
  private readonly roomMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.28, depthWrite: false });
  // A single DOM line surfacing the last placement reason code (created lazily so headless tests/
  // the non-DOM path never touch document).
  private reasonEl: HTMLDivElement | null = null;

  constructor() {
    this.group.add(this.placedGroup);
    this.group.add(this.ghostGroup);
    this.group.add(this.roomsGroup);
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    this.group.visible = v;
    if (this.reasonEl) this.reasonEl.style.display = v ? 'block' : 'none';
    if (!v) { this.clear(this.placedGroup); this.clear(this.ghostGroup); this.clearRooms(); }
  }

  // Rebuild the placed-piece gizmos from the live placed pieces: anchor dots + normal arrows,
  // occupied-slot markers, and a support bar. Cheap enough to call on every base change (placement
  // / collapse), not per frame.
  rebuild(placed: Iterable<PlacedPiece>): void {
    if (!this.enabled) return;
    this.clear(this.placedGroup);
    for (const p of placed) {
      const def: BuildPieceDef = pieceDef(p.defId);
      for (const a of hostAnchorsOf(p, def)) {
        const color = FAMILY_COLOR[a.family] ?? 0xffffff;
        const dot = new THREE.Mesh(this.dotGeo, new THREE.MeshBasicMaterial({ color }));
        dot.position.copy(a.worldPosition);
        this.placedGroup.add(dot);
        const arrow = new THREE.ArrowHelper(a.worldNormal.clone().normalize(), a.worldPosition, 0.5, color);
        this.placedGroup.add(arrow);
      }
      // Slot markers: the piece's own reserved slot(s) in red (occupied); a footprint's perimeter
      // wall-support edges in green (free attach points until a wall claims that height band).
      for (const s of slotPositions(p)) this.addSlotMarker(this.placedGroup, s.pos, s.taken ? SLOT_TAKEN : SLOT_FREE);
      // Support bar: green (1.0) fading to red (0) above the piece.
      const v = THREE.MathUtils.clamp(p.supportValue, 0, 1);
      const bar = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.5 * v + 0.02, 0.12),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(1 - v, v, 0) }),
      );
      bar.position.set(p.position.x, (p.top ?? p.position.y) + 0.4, p.position.z);
      this.placedGroup.add(bar);
    }
  }

  // Refresh the ghost's pending-slot preview + the reason line. Called every build update(): the
  // markers show the slots the NEXT placement would reserve, green when clear and red when the
  // placement is rejected (the reason code names exactly why). `pos` is the ghost center, used to
  // anchor a free-floating marker when a pending slot has no geometric position of its own.
  showGhost(pendingSlots: ResolvedSlot[], pos: THREE.Vector3, valid: boolean, reason: PlacementReason): void {
    this.lastReason = reason; // always tracked, even with debug off, so it is ready when toggled on
    if (!this.enabled) return;
    this.updateReasonEl(reason, valid);
    this.clear(this.ghostGroup);
    // One marker at the ghost center: green when the pending slots are clear (or the piece reserves
    // none, e.g. stairs/cupboard), red when the placement is rejected. The reason line names why.
    void pendingSlots;
    this.addSlotMarker(this.ghostGroup, pos, valid ? SLOT_FREE : SLOT_TAKEN);
  }

  // ROOM VIEW: one tinted quad per room cell at floor height, sealed rooms green / leaky orange,
  // label-free. Two instanced draws (squares + triangles), rebuilt on base changes only.
  showRooms(cells: readonly RoomDebugCell[]): void {
    if (!this.enabled) return;
    this.clearRooms();
    this.addRoomLayer(cells, 'square', this.roomSquareGeo);
    this.addRoomLayer(cells, 'triangle', this.roomTriGeo);
  }

  private addRoomLayer(cells: readonly RoomDebugCell[], shape: FootprintShape, geo: THREE.BufferGeometry): void {
    let n = 0;
    for (const c of cells) if (c.shape === shape) n++;
    if (n === 0) return;
    const im = new THREE.InstancedMesh(geo, this.roomMat, n);
    im.renderOrder = 2; // over the floor it tints
    const m = new THREE.Matrix4();
    const col = new THREE.Color();
    let i = 0;
    for (const c of cells) {
      if (c.shape !== shape) continue;
      m.makeRotationY(c.yaw).setPosition(c.x, c.top + 0.04, c.z);
      im.setMatrixAt(i, m);
      im.setColorAt(i, col.setHex(c.sealed ? ROOM_SEALED : ROOM_LEAKY));
      i++;
    }
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    this.roomsGroup.add(im);
  }

  // Instanced meshes own per-instance buffers (not shared geometry/material), so dispose() them --
  // the generic clear() would wrongly dispose the reused roomMat.
  private clearRooms(): void {
    for (let i = this.roomsGroup.children.length - 1; i >= 0; i--) {
      const o = this.roomsGroup.children[i] as THREE.InstancedMesh;
      this.roomsGroup.remove(o);
      o.dispose();
    }
  }

  private addSlotMarker(parent: THREE.Group, pos: THREE.Vector3, color: number): void {
    const m = new THREE.Mesh(this.slotGeo, new THREE.MeshBasicMaterial({ color, wireframe: true }));
    m.position.copy(pos);
    parent.add(m);
  }

  private updateReasonEl(reason: PlacementReason, valid: boolean): void {
    if (typeof document === 'undefined') return;
    if (!this.reasonEl) {
      const el = document.createElement('div');
      el.style.cssText = [
        'position:fixed', 'right:12px', 'bottom:12px', 'z-index:11',
        'padding:6px 10px', 'border-radius:6px',
        'background:rgba(8,11,15,0.82)', 'border:1px solid rgba(120,140,160,0.25)',
        'font:12px ui-monospace,Menlo,Consolas,monospace', 'pointer-events:none',
        this.enabled ? 'display:block' : 'display:none',
      ].join(';');
      document.body.appendChild(el);
      this.reasonEl = el;
    }
    this.reasonEl.textContent = `build  ${valid ? 'OK' : reason}`;
    this.reasonEl.style.color = valid ? '#9fe0a0' : '#e0a09f';
  }

  private clear(parent: THREE.Group): void {
    for (let i = parent.children.length - 1; i >= 0; i--) {
      const o = parent.children[i];
      parent.remove(o);
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh && mesh.material) (mesh.material as THREE.Material).dispose();
    }
  }
}

// Representative world positions of a placed piece's slots, tagged taken (red, genuinely reserved) or
// free (green, an open attach point). A footprint's own cell/top slot is reserved; its perimeter edge
// mids are wall-support slots that stay free until a wall claims that height band. Walls/doors mark
// their reserved slot at the piece center. Positions only — the authoritative occupancy is the
// string keys in the ledger; this just visualizes them.
function slotPositions(p: PlacedPiece): Array<{ pos: THREE.Vector3; taken: boolean }> {
  const out: Array<{ pos: THREE.Vector3; taken: boolean }> = [];
  const def = pieceDef(p.defId);
  const y = p.top ?? p.position.y;
  if (p.family !== 'none') {
    // A footprint piece (foundation / ceiling): its cell/top slot is genuinely occupied (red) at the
    // center; its perimeter edge slots are free wall-support attach points (green) at the edge mids.
    out.push({ pos: new THREE.Vector3(p.position.x, y, p.position.z), taken: true });
    for (const e of edgesFor(p.family as FootprintShape)) {
      const m = rotXZ(e.mx, e.mz, p.yaw);
      out.push({ pos: new THREE.Vector3(p.position.x + m.x, y, p.position.z + m.z), taken: false });
    }
  } else if (def.occupancy.length > 0) {
    // A none-family piece that reserves a slot (wall / doorway / window / door): mark its one
    // reserved slot red at the piece center (the piece itself IS the edge / leaf).
    out.push({ pos: new THREE.Vector3(p.position.x, p.position.y, p.position.z), taken: true });
  }
  return out;
}
